import { Controller } from "stimulus"

// Renderiza un gráfico con ApexCharts (mejor diseño + eje claro) a partir de un
// objeto de opciones serializado en JSON desde el servidor.
//
//   <div data-controller="apex-chart"
//        data-apex-chart-options-value="<%= options.to_json %>"></div>
//
// Las opciones aceptan claves custom (prefijo "_") que el controller traduce a
// funciones JS (que no se pueden serializar en JSON):
//   _yFormat: "money" | "moneyCompact"  → formatter de moneda en eje Y + tooltip
//
// ApexCharts se carga on-demand desde CDN (una sola vez por página).

const APEX_CDN = "https://cdn.jsdelivr.net/npm/apexcharts@3.49.1/dist/apexcharts.min.js"
let apexLoader = null

function loadApex() {
  if (window.ApexCharts) return Promise.resolve()
  if (apexLoader) return apexLoader
  apexLoader = new Promise((resolve, reject) => {
    const s = document.createElement("script")
    s.src = APEX_CDN
    s.async = true
    s.onload = () => resolve()
    s.onerror = () => { apexLoader = null; reject(new Error("No se pudo cargar ApexCharts")) }
    document.head.appendChild(s)
  })
  return apexLoader
}

const PALETTE = [
  "#2563eb", "#dc2626", "#f59e0b", "#16a34a", "#9333ea", "#0891b2", "#db2777",
  "#65a30d", "#ea580c", "#0d9488", "#4f46e5", "#ca8a04", "#be123c", "#15803d",
  "#7c3aed", "#0369a1", "#c026d3", "#84cc16", "#b45309", "#059669", "#1d4ed8",
  "#991b1b", "#a16207", "#166534", "#6d28d9", "#1e40af", "#9f1239", "#3f6212"
]

function moneyFull(n) {
  return "$" + Number(n).toLocaleString("es-AR", { maximumFractionDigits: 2 })
}

function moneyCompact(n) {
  const a = Math.abs(Number(n))
  if (a >= 1e6) return "$" + (n / 1e6).toLocaleString("es-AR", { maximumFractionDigits: 1 }) + " M"
  if (a >= 1e3) return "$" + (n / 1e3).toLocaleString("es-AR", { maximumFractionDigits: 0 }) + " k"
  return "$" + Number(n).toLocaleString("es-AR", { maximumFractionDigits: 0 })
}

export default class extends Controller {
  static values = { options: Object }
  static targets = ["canvas", "paretoButton"]

  connect() {
    loadApex()
      .then(() => this.render())
      .catch((e) => { this.container.innerHTML = `<div class="text-muted small">${e.message}</div>` })
  }

  disconnect() {
    if (this.chart) { this.chart.destroy(); this.chart = null }
  }

  // Contenedor del gráfico: target explícito (cuando el controller envuelve
  // también la toolbar, ej. /gastos) o el propio elemento (uso histórico).
  get container() {
    return this.hasCanvasTarget ? this.canvasTarget : this.element
  }

  // Pareto 80/20: muestra solo las series más grandes que acumulan el 80% del
  // total y oculta el resto. Segundo click restaura todas.
  togglePareto() {
    if (!this.chart) return
    this.paretoOn = !this.paretoOn

    const series = (this.optionsValue.series || [])
    if (this.paretoOn) {
      const totals = series
        .map((s) => ({ name: s.name, total: (s.data || []).reduce((a, v) => a + (Number(v) || 0), 0) }))
        .sort((a, b) => b.total - a.total)
      const grand = totals.reduce((a, t) => a + t.total, 0)
      const keep = new Set()
      let acc = 0
      for (const t of totals) {
        if (acc >= grand * 0.8) break
        keep.add(t.name)
        acc += t.total
      }
      series.forEach((s) => { keep.has(s.name) ? this.chart.showSeries(s.name) : this.chart.hideSeries(s.name) })
    } else {
      series.forEach((s) => this.chart.showSeries(s.name))
    }

    if (this.hasParetoButtonTarget) {
      this.paretoButtonTarget.classList.toggle("active", this.paretoOn)
    }
  }

  render() {
    const opts = JSON.parse(JSON.stringify(this.optionsValue || {}))

    // Defaults de diseño.
    opts.chart = Object.assign({ fontFamily: "inherit", animations: { enabled: true } }, opts.chart)
    if (!opts.colors) opts.colors = PALETTE
    if (!opts.grid) opts.grid = { borderColor: "#eef0f3", strokeDashArray: 3 }
    if (!opts.legend) opts.legend = { position: "bottom" }

    // Formatters de moneda (no serializables en JSON).
    const yFormat = opts._yFormat
    delete opts._yFormat
    if (yFormat) {
      const axisFmt = yFormat === "moneyCompact" ? moneyCompact : moneyFull
      opts.yaxis = opts.yaxis || {}
      opts.yaxis.labels = Object.assign({}, opts.yaxis.labels, { formatter: axisFmt })
      opts.tooltip = opts.tooltip || {}
      opts.tooltip.y = Object.assign({}, opts.tooltip.y, { formatter: moneyFull })
    }

    this.container.innerHTML = ""
    this.chart = new window.ApexCharts(this.container, opts)
    this.chart.render()
  }
}
